import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import ReactMarkdown from 'react-markdown';
import html2canvas from 'html2canvas';
import { X as IconX, FileText, Map as IconMap, Paperclip, MessageSquare, Edit, Trash2, Send, UploadCloud, Download, Loader, Save, AlertTriangle, ExternalLink, MapPin, Copy, Camera, Palette, History, CheckCircle, Clock, XCircle, Search, ClipboardList, Bold, Italic, Underline, Code, Strikethrough, List, ListOrdered, Quote, Smile, Link2 } from 'lucide-react';

// --- CONSTANTES E COMPONENTES IMPORTADOS ---
import { PROJECT_STATUSES } from '../../constants/appConstants';
import UserAvatar from '../common/UserAvatar';
import StyledFileInput from '../common/StyledFileInput';
import ConfirmDialog from '../common/ConfirmDialog';
import { logActivity, getActivityLog } from '../../services/activityLogService';
import { getFirestore, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';


const createStatusIcon = (status) => {
    const statusInfo = PROJECT_STATUSES.find(s => s.value.toLowerCase() === (status || '').toLowerCase());
    const color = statusInfo ? statusInfo.color : '#6b7280';

    return new L.DivIcon({
        html: `<svg viewBox="0 0 24 24" width="30" height="30" fill="${color}" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="white" stroke-width="1"/></svg>`,
        className: 'bg-transparent border-0',
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -30]
    });
};

const MapEvents = ({ onContextMenu }) => {
    useMapEvents({
        contextmenu(e) {
            onContextMenu(e);
        },
    });
    return null;
};


// --- COMPONENTE DE TIMELINE DO STATUS DO PROJETO ---
const ProjectStatusTimeline = ({ statusHistory, usersMap, currentStatus }) => {
    // Definir a ordem e detalhes de cada status
    const statusFlow = [
        { value: 'Comercial', label: 'Comercial', icon: FileText },
        { value: 'Viabilidade', label: 'Viabilidade', icon: Search },
        { value: 'Em análise', label: 'Análise Técnica', icon: Clock },
        { value: 'Análise Aprovada', label: 'Análise Técnica Aprovada', icon: CheckCircle },
        { value: 'Em vistoria', label: 'Em vistoria', icon: ClipboardList },
        { value: 'Aprovado', label: 'Vistoria Aprovada', icon: CheckCircle },
    ];

    // O status atual determina o progresso, ou o último status válido se Reprovado.
    const isReproved = currentStatus === 'Reprovado';

    // Determinar o último status válido (antes de ser reprovado)
    let lastValidStatusValue = currentStatus;
    if (isReproved) {
        // Find the last status before 'Reprovado'
        for (let i = statusHistory.length - 1; i >= 0; i--) {
            const historyItem = statusHistory[i];
            const toStatus = historyItem.details?.statusChange?.to || (historyItem.activityType === 'create' ? 'Comercial' : null);
            if (toStatus && toStatus !== 'Reprovado') {
                lastValidStatusValue = toStatus;
                break;
            }
        }
    }

    const currentStatusIndex = statusFlow.findIndex(s => s.value === lastValidStatusValue);

    // Calcular percentual de progresso
    // 6 status. index 0 -> 0%, index 5 -> 100%
    const progressPercentage = currentStatusIndex >= 0 ? (currentStatusIndex / (statusFlow.length - 1)) * 100 : 0;

    return (
        <div className="w-full">
            {/* Progress Bar */}
            <div className="mb-10 px-4">
                <div className="flex justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Progresso do Projeto</span>
                    <span className={`text-sm font-bold ${isReproved ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                        {isReproved ? 'Reprovado' : `${Math.round(progressPercentage)}%`}
                    </span>
                </div>
                <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden relative">
                    <div
                        className={`h-full rounded-full transition-all duration-500 ${isReproved ? 'bg-red-500' : 'bg-blue-500'}`}
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>
                {isReproved && (
                    <p className="text-xs text-red-500 mt-2 flex items-center">
                        <XCircle className="w-4 h-4 mr-1" />
                        O projeto foi reprovado após a etapa de {statusFlow[currentStatusIndex]?.label || 'Desconhecida'}.
                    </p>
                )}
            </div>

            {/* Timeline Vertical */}
            {statusHistory.length === 0 ? (
                <p className="text-center text-slate-500 my-8">Nenhum registro de alteração de status encontrado.</p>
            ) : (
                <div className="relative border-l-2 border-slate-200 dark:border-slate-700 ml-6 pl-6 space-y-8 mt-6">
                    {[...statusHistory].reverse().map((activity, index) => {
                        const isCreation = activity.activityType === 'create';
                        const fromStatus = activity.details?.statusChange?.from;
                        const toStatus = isCreation ? 'Comercial' : activity.details?.statusChange?.to;

                        const statusInfo = statusFlow.find(s => s.value === toStatus) || { label: toStatus, icon: Clock };
                        const isReprovedStep = toStatus === 'Reprovado';

                        // Select icon and color
                        const StepIcon = isReprovedStep ? XCircle : statusInfo.icon;
                        const iconBgColor = isReprovedStep ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400' :
                                         index === 0 && !isReproved ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' :
                                         'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400';
                        const iconBorder = isReprovedStep ? 'border-red-500' :
                                          index === 0 && !isReproved ? 'border-blue-500' :
                                          'border-slate-300 dark:border-slate-600';

                        const user = usersMap.get(activity.userId);

                        return (
                            <div key={activity.timestamp || index} className="relative">
                                {/* Timeline Node */}
                                <div className={`absolute -left-10 mt-1.5 w-8 h-8 rounded-full border-2 flex items-center justify-center bg-white dark:bg-slate-900 ${iconBorder} ${iconBgColor}`}>
                                    <StepIcon className="w-4 h-4" />
                                </div>

                                {/* Content */}
                                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className={`font-bold ${isReprovedStep ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                                {isCreation ? 'Projeto Criado' : 'Status Alterado'}
                                            </h4>
                                            <p className="text-sm font-medium mt-1 text-slate-700 dark:text-slate-300">
                                                {isCreation ? (
                                                    <span>Status inicial: <span className="font-bold">{statusInfo.label}</span></span>
                                                ) : (
                                                    <span>
                                                        <span className="text-slate-500 line-through mr-2">{statusFlow.find(s=>s.value===fromStatus)?.label || fromStatus}</span>
                                                        <span className="font-bold">{isReprovedStep ? 'Reprovado' : statusInfo.label}</span>
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-xs font-semibold text-slate-500 block">
                                                {new Date(activity.timestamp).toLocaleDateString('pt-BR')}
                                            </span>
                                            <span className="text-xs text-slate-400 block">
                                                {new Date(activity.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center mt-3 pt-3 border-t border-slate-200 dark:border-slate-700/50">
                                        <UserAvatar user={user} size="h-6 w-6" />
                                        <span className="ml-2 text-xs font-medium text-slate-600 dark:text-slate-400">
                                            por {user?.displayName || 'Usuário Desconhecido'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const CommentToolbar = ({ textareaRef, textSetter, emojis, insertFormatting, insertEmoji, setActiveAttachmentSetter, setActiveAttachmentTextareaRef, setShowAttachmentPopover }) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    
    return (
        <div className="flex flex-wrap items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-t-lg">
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'bold')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Negrito"><Bold className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'italic')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Itálico"><Italic className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'underline')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Sublinhado"><Underline className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'code')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Código"><Code className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'strikethrough')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Tachado"><Strikethrough className="w-3.5 h-3.5" /></button>
            <div className="h-4 w-[1px] bg-slate-300 dark:bg-slate-600 mx-1" />
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'list-bullet')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Lista com Marcadores"><List className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'list-ordered')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Lista Numerada"><ListOrdered className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'quote')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Citação"><Quote className="w-3.5 h-3.5" /></button>
            <div className="h-4 w-[1px] bg-slate-300 dark:bg-slate-600 mx-1" />
            <button type="button" onClick={() => insertFormatting(textareaRef, textSetter, 'link')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Inserir Link"><Link2 className="w-3.5 h-3.5" /></button>
            
            <div className="relative">
                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300" title="Inserir Emoji"><Smile className="w-3.5 h-3.5" /></button>
                {showEmojiPicker && (
                    <div className="absolute z-[100] bottom-8 left-0 flex bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-1.5 gap-1">
                        {emojis.map(emoji => (
                            <button key={emoji} type="button" onClick={() => { insertEmoji(textareaRef, textSetter, emoji); setShowEmojiPicker(false); }} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-sm">{emoji}</button>
                        ))}
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={() => {
                    setActiveAttachmentSetter(() => textSetter);
                    setActiveAttachmentTextareaRef(textareaRef);
                    setShowAttachmentPopover(true);
                }}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-blue-500 dark:text-blue-400 font-bold flex items-center gap-1 text-[11px]"
                title="Anexar item do sistema"
            >
                <Paperclip className="w-3.5 h-3.5" />
                Anexar Item
            </button>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL DO MODAL ---

function ProjectModal({ project, onClose, onSave, onUpdate, onDelete, onReplacePoles, cities, isProcessing, userRole, onFileDownload, onAddComment, onUpdateComment, onDeleteComment, onDeleteFile, currentUser, usersMap, theme, darkMapEnabled, allProjects = [], allPendencies = [], onOpenProject, onOpenPendency }) {
    const isViewMode = !!project;
    const isAdm = userRole === 'administrador';
    const projectIdRef = useRef(null);
    
    // Referências para os textareas
    const newCommentTextareaRef = useRef(null);
    const replyTextareaRef = useRef(null);
    const editCommentTextareaRef = useRef(null);

    // Estados de menções e comentários aninhados
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [selectedUserIndex, setSelectedUserIndex] = useState(0);
    const [activeTextareaRef, setActiveTextareaRef] = useState(null);
    const [activeTextSetter, setActiveTextSetter] = useState(null);
    const [replyingToCommentId, setReplyingToCommentId] = useState(null);
    const [replyCommentText, setReplyCommentText] = useState('');

    // Estados para anexar itens do sistema
    const [showAttachmentPopover, setShowAttachmentPopover] = useState(false);
    const [attachmentType, setAttachmentType] = useState('project'); // 'project' ou 'pendency'
    const [attachmentSearch, setAttachmentSearch] = useState('');
    const [activeAttachmentSetter, setActiveAttachmentSetter] = useState(null);
    const [activeAttachmentTextareaRef, setActiveAttachmentTextareaRef] = useState(null);

    // Emojis e lista de usuários
    const emojis = ['👍', '❤️', '😂', '🎉', '🚀', '👀', '💬', '✅', '⚠️', '❌'];
    
    const usersList = React.useMemo(() => {
        return usersMap ? Array.from(usersMap.values()) : [];
    }, [usersMap]);

    const filteredUsers = React.useMemo(() => {
        if (!mentionSearch) return usersList;
        const searchLower = mentionSearch.toLowerCase();
        return usersList.filter(user => 
            (user.displayName && user.displayName.toLowerCase().includes(searchLower)) ||
            (user.email && user.email.toLowerCase().includes(searchLower))
        );
    }, [usersList, mentionSearch]);

    const filteredAttachments = React.useMemo(() => {
        const search = attachmentSearch.toLowerCase();
        if (attachmentType === 'project') {
            return allProjects.filter(p => 
                p.id !== project?.id &&
                ((p.protocolo && p.protocolo.toLowerCase().includes(search)) ||
                 (p.cidade && p.cidade.toLowerCase().includes(search)) ||
                 (p.local && p.local.toLowerCase().includes(search)))
            );
        } else {
            return allPendencies.filter(p => 
                (p.descricao && p.descricao.toLowerCase().includes(search)) ||
                (p.cidade && p.cidade.toLowerCase().includes(search))
            );
        }
    }, [allProjects, allPendencies, attachmentSearch, attachmentType, project?.id]);

    const [formData, setFormData] = useState({});
    const [activeModalTab, setActiveModalTab] = useState('info');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [statusHistory, setStatusHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
const [downloading, setDownloading] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [editingComment, setEditingComment] = useState(null);
    const [newFiles, setNewFiles] = useState([]);
    const [newCategorizedFiles, setNewCategorizedFiles] = useState({});
    const [files, setFiles] = useState({ csvFile: null, projeto: null, autorizacao: null, vistoria: null, outros: [] });
    
    const [newCsvFile, setNewCsvFile] = useState(null);
    const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
    const [localMapTheme, setLocalMapTheme] = useState(null);

    // Estados para o Menu de Contexto
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, lat: 0, lng: 0 });
    const [feedback, setFeedback] = useState({ visible: false, message: '', x: 0, y: 0 });
    const mapContainerRef = useRef(null);

    useEffect(() => {
        if (isViewMode && project) {
            logActivity('view', 'project', project.id, null, project.protocolo);

            // Replaces the onProjectView Firestore trigger
            if (currentUser?.uid && project.id) {
                const db = getFirestore();
                const projectRef = doc(db, `artifacts/dashboard-ftth/public/data/projects/${project.id}`);
                updateDoc(projectRef, {
                    viewers: arrayUnion(currentUser.uid)
                }).catch(err => console.error("Falha ao atualizar visualizadores do projeto:", err));
            }

            projectIdRef.current = project.id;
            setFormData({
                protocolo: project.protocolo || '',
                cidade: project.cidade || '',
                local: project.local || '',
                totalPostes: project.totalPostes || 0,
                status: project.status || PROJECT_STATUSES[0].value,
                statusDate: project.statusDate ? new Date(project.statusDate).toISOString().split('T')[0] : '',
                dueDate: project.dueDate ? new Date(project.dueDate).toISOString().split('T')[0] : '',
            });
        } else {
            projectIdRef.current = null;
            setFormData({
                protocolo: '',
                cidade: '',
                local: '',
                totalPostes: 0,
                status: PROJECT_STATUSES[0].value,
                statusDate: '',
                dueDate: '',
            });
        }
        setNewCsvFile(null);
        setShowReplaceConfirm(false);
        setLocalMapTheme(null);
    }, [project, isViewMode, cities, currentUser.uid]);


    useEffect(() => {
        if (isViewMode && activeModalTab === 'historico' && project?.id) {
            const fetchHistory = async () => {
                setIsHistoryLoading(true);
                const log = await getActivityLog('project', project.id);
                // Filter and keep status changes + creation
                const statusLog = log
                    .filter(activity =>
                        activity.activityType === 'create' ||
                        (activity.activityType === 'update' && activity.details?.statusChange)
                    )
                    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()); // oldest first to build timeline bottom-up later
                setStatusHistory(statusLog);
                setIsHistoryLoading(false);
            };
            fetchHistory();
        }
    }, [isViewMode, activeModalTab, project?.id]);
const isGlobalDark = theme === 'dark' && darkMapEnabled;
    const isMapDark = localMapTheme !== null ? localMapTheme === 'dark' : isGlobalDark;
    const tileUrl = getMapTileUrl(isMapDark);
    const tileAttribution = getMapTileAttribution(isMapDark);



    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleNewFilesChange = (e) => {
        setNewFiles(Array.from(e.target.files));
    };
    
    const handleCategorizedFileChange = (e, category) => {
        const file = e.target.files[0];
        if (file) {
            setNewCategorizedFiles(prev => ({ ...prev, [category]: file }));
        }
    };
    
    const handleFileChange = (e, category) => {
        const newFiles = e.target.files;
        if (category === 'outros') {
            // Substitui o array de 'outros' com o novo array de arquivos.
            // Isso funciona tanto para adicionar quanto para remover do StyledFileInput.
            setFiles(prev => ({ ...prev, outros: Array.from(newFiles) }));
        } else {
            // Define o arquivo como o primeiro da lista ou nulo se a lista estiver vazia (após remoção).
            setFiles(prev => ({ ...prev, [category]: newFiles[0] || null }));
        }
    };

    // --- FUNÇÕES DO MENU DE CONTEXTO ---

    const handleContextMenu = (e) => {
        e.originalEvent.preventDefault();
        setContextMenu({
            visible: true,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
            lat: e.latlng.lat,
            lng: e.latlng.lng
        });
    };

    const closeContextMenu = () => {
        setContextMenu({ ...contextMenu, visible: false });
    };

    const toggleMapMode = () => {
        setLocalMapTheme(prev => {
            if (prev === null) return isGlobalDark ? 'light' : 'dark';
            return prev === 'dark' ? 'light' : 'dark';
        });
        closeContextMenu();
    };

    const showFeedbackMessage = (message, x, y) => {
        setFeedback({ visible: true, message, x, y });
        setTimeout(() => setFeedback(prev => ({ ...prev, visible: false })), 2000);
    };

    const handleOpenGoogleMaps = () => {
        const { lat, lng } = contextMenu;
        window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
        closeContextMenu();
    };

    const handleOpenStreetView = () => {
        const { lat, lng } = contextMenu;
        window.open(`https://www.google.com/maps?layer=c&cbll=${lat},${lng}`, '_blank');
        closeContextMenu();
    };

    const handleCopyCoordinates = () => {
        const { lat, lng, x, y } = contextMenu;
        const text = `${lat}, ${lng}`;
        navigator.clipboard.writeText(text).then(() => {
            showFeedbackMessage('Copiado!', x, y);
        }).catch(err => console.error('Falha ao copiar:', err));
        closeContextMenu();
    };

    const handleScreenshot = async () => {
        if (!mapContainerRef.current) return;

        // Esconde o menu para não sair no print
        setContextMenu(prev => ({ ...prev, visible: false }));

        try {
            const canvas = await html2canvas(mapContainerRef.current, {
                useCORS: true,
                allowTaint: true,
                logging: false
            });

            canvas.toBlob((blob) => {
                if (!blob) return;
                try {
                    const item = new ClipboardItem({ 'image/png': blob });
                    navigator.clipboard.write([item]).then(() => {
                        showFeedbackMessage('Copiado!', contextMenu.x, contextMenu.y);
                    });
                } catch (err) {
                    console.error('Falha ao copiar imagem:', err);
                    // Fallback para navegadores que não suportam ClipboardItem totalmente ou contexto inseguro
                    alert('Não foi possível copiar a imagem para a área de transferência. Verifique as permissões.');
                }
            });
        } catch (error) {
            console.error('Erro ao tirar screenshot:', error);
        }
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isAdm) return; 

        if (isViewMode) {
            const id = projectIdRef.current;
            if (!id) {
                console.error("Erro Crítico: Tentativa de atualizar sem um ID de projeto. A operação foi cancelada.");
                return;
            }
            const { totalPostes, ...updateData } = formData;
            onUpdate(id, updateData, newFiles, newCategorizedFiles);
            setNewFiles([]);
            setNewCategorizedFiles({});
        } else {
            onSave({ ...formData, files });
        }
    };

    const handleConfirmReplacePoles = () => {
        if (!newCsvFile || !project?.id) return;
        onReplacePoles(project.id, newCsvFile);
        setShowReplaceConfirm(false);
        setNewCsvFile(null);
    };
    
    // Helpers de redirecionamento de link
    const handleOpenProjectFromLink = React.useCallback((projId) => {
        if (!allProjects || !onOpenProject) return;
        const targetProject = allProjects.find(p => p.id === projId);
        if (targetProject) {
            onOpenProject(targetProject);
        } else {
            alert('Projeto associado ao anexo não foi encontrado ou foi deletado.');
        }
    }, [allProjects, onOpenProject]);

    const handleOpenPendencyFromLink = React.useCallback((pendId) => {
        if (!allPendencies || !onOpenPendency) return;
        const targetPendency = allPendencies.find(p => p.id === pendId);
        if (targetPendency) {
            onClose(); // fecha o modal de projetos atual
            onOpenPendency(targetPendency);
        } else {
            alert('Pendência associada ao anexo não foi encontrada ou foi deletada.');
        }
    }, [allPendencies, onOpenPendency, onClose]);

    // Componentes personalizados para o ReactMarkdown
    const markdownComponents = React.useMemo(() => ({
        a: ({ href, children, ...props }) => {
            if (href === 'u-link') {
                return <u>{children}</u>;
            }
            if (href === 'del-link') {
                return <del>{children}</del>;
            }
            if (href && href.startsWith('https://mention.user/')) {
                const name = decodeURIComponent(href.substring(21));
                return (
                    <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-bold px-1.5 py-0.5 rounded text-xs inline-block mx-0.5 select-none">
                        @{name}
                    </span>
                );
            }
            if (href && (href.startsWith('https://project.link/') || href.startsWith('project:'))) {
                const projId = href.startsWith('project:') ? href.substring(8) : href.substring(21);
                return (
                    <button
                        type="button"
                        onClick={() => handleOpenProjectFromLink(projId)}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-semibold text-[11px] my-0.5 cursor-pointer shadow-sm select-none"
                    >
                        <FileText className="w-3 h-3" />
                        {children}
                    </button>
                );
            }
            if (href && (href.startsWith('https://pendency.link/') || href.startsWith('pendency:'))) {
                const pendId = href.startsWith('pendency:') ? href.substring(9) : href.substring(22);
                return (
                    <button
                        type="button"
                        onClick={() => handleOpenPendencyFromLink(pendId)}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors font-semibold text-[11px] my-0.5 cursor-pointer shadow-sm select-none"
                    >
                        <AlertTriangle className="w-3 h-3" />
                        {children}
                    </button>
                );
            }
            return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline font-semibold" {...props}>{children}</a>;
        },
        p: ({ children }) => <p className="my-1 leading-relaxed">{children}</p>,
        li: ({ children }) => <li className="my-0.5 leading-relaxed">{children}</li>,
        blockquote: ({ children }) => <blockquote className="border-l-4 border-slate-300 dark:border-slate-600 pl-3 italic my-2 text-slate-500 dark:text-slate-400">{children}</blockquote>,
        h1: ({ children }) => <h1 className="text-base font-bold my-1 text-slate-800 dark:text-slate-200">{children}</h1>,
        h2: ({ children }) => <h2 className="text-sm font-bold my-1 text-slate-800 dark:text-slate-200">{children}</h2>,
        h3: ({ children }) => <h3 className="text-xs font-bold my-1 text-slate-800 dark:text-slate-200">{children}</h3>,
    }), [handleOpenProjectFromLink, handleOpenPendencyFromLink]);

    const insertFormatting = (textareaRef, textSetter, type) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);

        let replacement = '';
        let cursorOffset = 0;

        // Função auxiliar para remover espaços nas pontas e mantê-los fora da formatação
        const trimSpaces = (str) => {
            const leadingMatch = str.match(/^\s*/);
            const trailingMatch = str.match(/\s*$/);
            const leading = leadingMatch ? leadingMatch[0] : '';
            const trailing = trailingMatch ? trailingMatch[0] : '';
            const trimmed = str.trim();
            return { leading, trimmed, trailing };
        };

        switch (type) {
            case 'bold': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}**${trimmed}**${trailing}`;
                cursorOffset = selectedText ? 0 : 2 + trailing.length;
                break;
            }
            case 'italic': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}*${trimmed}*${trailing}`;
                cursorOffset = selectedText ? 0 : 1 + trailing.length;
                break;
            }
            case 'underline': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}<u>${trimmed}</u>${trailing}`;
                cursorOffset = selectedText ? 0 : 4 + trailing.length;
                break;
            }
            case 'code': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}\`${trimmed}\`${trailing}`;
                cursorOffset = selectedText ? 0 : 1 + trailing.length;
                break;
            }
            case 'strikethrough': {
                const { leading, trimmed, trailing } = trimSpaces(selectedText || 'texto');
                replacement = `${leading}~~${trimmed}~~${trailing}`;
                cursorOffset = selectedText ? 0 : 2 + trailing.length;
                break;
            }
            case 'quote':
                replacement = `\n> ${selectedText || 'citação'}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            case 'list-bullet': {
                const lines = (selectedText || 'item').split('\n');
                const formattedLines = lines.map(line => {
                    // Evita adicionar o marcador duplicado se a linha já possuir um
                    if (/^\s*[-*]\s/.test(line)) return line;
                    return `- ${line}`;
                });
                replacement = `\n${formattedLines.join('\n')}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            }
            case 'list-ordered': {
                const lines = (selectedText || 'item').split('\n');
                const formattedLines = lines.map((line, idx) => {
                    // Evita adicionar o marcador duplicado se a linha já possuir um número
                    if (/^\s*\d+\.\s/.test(line)) return line;
                    return `${idx + 1}. ${line}`;
                });
                replacement = `\n${formattedLines.join('\n')}\n`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            }
            case 'link':
                replacement = `[${selectedText || 'Link'}](https://)`;
                cursorOffset = selectedText ? 0 : 1;
                break;
            default:
                return;
        }

        const newText = text.substring(0, start) + replacement + text.substring(end);
        textSetter(newText);

        setTimeout(() => {
            textarea.focus();
            if (selectedText) {
                textarea.setSelectionRange(start, start + replacement.length);
            } else {
                const pos = start + replacement.length - cursorOffset;
                textarea.setSelectionRange(pos, pos);
            }
        }, 0);
    };

    const insertEmoji = (textareaRef, textSetter, emoji) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        const newText = text.substring(0, start) + emoji + text.substring(end);
        textSetter(newText);

        setTimeout(() => {
            textarea.focus();
            const pos = start + emoji.length;
            textarea.setSelectionRange(pos, pos);
        }, 0);
    };

    const handleInsertAttachment = (item) => {
        const textarea = activeAttachmentTextareaRef?.current;
        if (!textarea || !activeAttachmentSetter) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;

        let markdown = '';
        if (attachmentType === 'project') {
            markdown = `[📎 Projeto: ${item.protocolo} - ${item.cidade || ''}](https://project.link/${item.id})`;
        } else {
            const pendencyIdStr = item.id.substring(0, 5).toUpperCase();
            markdown = `[📎 Pendência: #${pendencyIdStr} - ${item.descricao.substring(0, 20)}...](https://pendency.link/${item.id})`;
        }

        const newText = text.substring(0, start) + markdown + text.substring(end);
        activeAttachmentSetter(newText);
        setShowAttachmentPopover(false);
        setAttachmentSearch('');

        setTimeout(() => {
            textarea.focus();
            const pos = start + markdown.length;
            textarea.setSelectionRange(pos, pos);
        }, 0);
    };

    const handleTextareaChange = (e, textSetter, textareaRef) => {
        const text = e.target.value;
        textSetter(text);
        setActiveTextareaRef(textareaRef);
        setActiveTextSetter(() => textSetter);
        
        const selectionStart = e.target.selectionStart;
        const textBeforeCursor = text.substring(0, selectionStart);
        const lastAtIdx = textBeforeCursor.lastIndexOf('@');
        
        if (lastAtIdx !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIdx + 1);
            const charBeforeAt = lastAtIdx > 0 ? textBeforeCursor[lastAtIdx - 1] : '';
            const isValidStart = lastAtIdx === 0 || /\s/.test(charBeforeAt);
            
            if (isValidStart && !/\s/.test(textAfterAt)) {
                setShowMentionDropdown(true);
                setMentionSearch(textAfterAt);
                setMentionStartIndex(lastAtIdx);
                setSelectedUserIndex(0);
                return;
            }
        }
        setShowMentionDropdown(false);
    };

    const handleTextareaKeyDown = (e, filteredUsers, textSetter) => {
        if (showMentionDropdown && filteredUsers.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedUserIndex(prev => (prev + 1) % filteredUsers.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedUserIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleSelectMention(filteredUsers[selectedUserIndex]);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setShowMentionDropdown(false);
            }
            return;
        }

        // Se pressionar Enter ou Shift + Enter sem o autocomplete ativo, trata listas ordenadas e marcadores
        if (e.key === 'Enter' && textSetter) {
            const textarea = e.target;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = textarea.value;

            // Encontra a linha em que o cursor está atualmente
            const beforeCursor = text.substring(0, start);
            const lastNewLine = beforeCursor.lastIndexOf('\n');
            const currentLine = beforeCursor.substring(lastNewLine + 1);

            // Expressões regulares para correspondência de listas marcadas e numeradas
            const bulletRegex = /^(\s*[-*]\s)(.*)/;
            const orderedRegex = /^(\s*(\d+)\.\s)(.*)/;

            const bulletMatch = currentLine.match(bulletRegex);
            const orderedMatch = currentLine.match(orderedRegex);

            if (bulletMatch || orderedMatch) {
                if (e.shiftKey) {
                    // Shift + Enter: Quebra a linha física inserindo o recuo necessário sob o mesmo item da lista
                    e.preventDefault();
                    const indentSize = bulletMatch ? bulletMatch[1].length : orderedMatch[1].length;
                    const indent = ' '.repeat(indentSize);
                    const replacement = '\n' + indent;

                    const newText = text.substring(0, start) + replacement + text.substring(end);
                    textSetter(newText);

                    setTimeout(() => {
                        textarea.focus();
                        const newPos = start + replacement.length;
                        textarea.setSelectionRange(newPos, newPos);
                    }, 0);
                } else {
                    // Enter padrão: Cria um novo item de lista ou remove o marcador se estiver vazio
                    e.preventDefault();
                    const content = bulletMatch ? bulletMatch[2] : orderedMatch[3];
                    
                    if (content.trim() === '') {
                        // Se o item estiver vazio, remove o marcador e sai da lista
                        const beforeLine = text.substring(0, lastNewLine + 1);
                        const afterCursor = text.substring(start);
                        const newText = beforeLine + afterCursor;
                        textSetter(newText);

                        setTimeout(() => {
                            textarea.focus();
                            const newPos = lastNewLine + 1;
                            textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                    } else {
                        // Senão, insere o próximo marcador na linha de baixo
                        let nextMarker = '';
                        if (bulletMatch) {
                            nextMarker = '\n' + bulletMatch[1];
                        } else {
                            const currentNum = parseInt(orderedMatch[2], 10);
                            const nextNum = currentNum + 1;
                            const spacesMatch = orderedMatch[1].match(/^\s*/);
                            const initialSpaces = spacesMatch ? spacesMatch[0] : '';
                            nextMarker = `\n${initialSpaces}${nextNum}. `;
                        }

                        const newText = text.substring(0, start) + nextMarker + text.substring(end);
                        textSetter(newText);

                        setTimeout(() => {
                            textarea.focus();
                            const newPos = start + nextMarker.length;
                            textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                    }
                }
            }
        }
    };

    const handleSelectMention = (user) => {
        const textarea = activeTextareaRef?.current;
        if (!textarea || !activeTextSetter) return;
        
        const text = textarea.value;
        const selectionStart = textarea.selectionStart;
        const beforeMention = text.substring(0, mentionStartIndex);
        const afterMention = text.substring(selectionStart);
        const mentionText = `@${user.displayName} `;
        const newText = beforeMention + mentionText + afterMention;
        
        activeTextSetter(newText);
        setShowMentionDropdown(false);
        
        setTimeout(() => {
            textarea.focus();
            const cursorPosition = mentionStartIndex + mentionText.length;
            textarea.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
    };

    const extractMentionedUids = React.useCallback((text) => {
        if (!text) return [];
        return usersList
            .filter(user => user.displayName && text.includes(`@${user.displayName}`))
            .map(user => user.id || user.uid)
            .filter(Boolean);
    }, [usersList]);

    // Função de pré-processamento para transformar marcações customizadas em links do Markdown compatíveis
    const preProcessText = React.useCallback((text) => {
        if (!text) return '';
        let processed = text;
        
        // Substitui <u>conteúdo</u> por [conteúdo](u-link)
        processed = processed.replace(/<u>(.*?)<\/u>/g, '[$1](u-link)');
        
        // Substitui <del>conteúdo</del> ou ~~conteúdo~~ por [conteúdo](del-link)
        processed = processed.replace(/<del>(.*?)<\/del>/g, '[$1](del-link)');
        processed = processed.replace(/~~(.*?)~~/g, '[$1](del-link)');
        
        // Substitui as menções aos usuários por links de menção estruturados codificados seguros
        usersList.forEach(user => {
            if (user.displayName) {
                const name = user.displayName;
                const escapedName = name.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                const regex = new RegExp(`@${escapedName}\\b`, 'g');
                // Codifica o nome para evitar espaços e usa protocolo HTTPS para evitar rejeições de segurança do parser de markdown
                processed = processed.replace(regex, `[@${name}](https://mention.user/${encodeURIComponent(name)})`);
            }
        });
        
        return processed;
    }, [usersList]);

    const handleCommentSubmit = (parentId = null, text = newComment, textSetter = setNewComment) => {
        const id = projectIdRef.current;
        if (text.trim() === '' || !id) return;
        
        const mentionedUids = extractMentionedUids(text);
        onAddComment(id, text, parentId, mentionedUids);
        textSetter('');
        setReplyingToCommentId(null);
    };

    const handleEditComment = (comment) => {
        setEditingComment({ ...comment });
    };

    const handleSaveEditedComment = () => {
        const id = projectIdRef.current;
        if (!editingComment || editingComment.text.trim() === '' || !id) return;
        
        const mentionedUids = extractMentionedUids(editingComment.text);
        onUpdateComment(id, editingComment, mentionedUids);
        setEditingComment(null); // Fecha o modo de edição do comentário
    };

    // Agrupa os comentários pelo parentId para criar a estrutura em árvore
    const organizedComments = React.useMemo(() => {
        if (!project?.comments) return [];
        
        const commentsList = [...project.comments];
        const commentsMap = {};
        const rootComments = [];
        
        commentsList.forEach(c => {
            const id = c.id || c.createdAt;
            commentsMap[id] = { ...c, replies: [] };
        });
        
        commentsList.forEach(c => {
            const id = c.id || c.createdAt;
            const current = commentsMap[id];
            
            if (c.parentId && commentsMap[c.parentId]) {
                commentsMap[c.parentId].replies.push(current);
            } else {
                rootComments.push(current);
            }
        });
        
        const sortByDate = (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        
        rootComments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        
        rootComments.forEach(root => {
            root.replies.sort(sortByDate);
            root.replies.forEach(reply => {
                reply.replies.sort(sortByDate);
            });
        });
        
        return rootComments;
    }, [project?.comments]);

    const renderMentionDropdown = (textareaRef) => {
        if (showMentionDropdown && activeTextareaRef === textareaRef && filteredUsers.length > 0) {
            return (
                <div className="absolute z-[200] max-h-40 w-64 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl mt-1 scrollbar-thin">
                    {filteredUsers.map((user, idx) => (
                        <button
                            key={user.uid}
                            type="button"
                            onClick={() => handleSelectMention(user)}
                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${idx === selectedUserIndex ? 'bg-blue-500 text-white font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'}`}
                        >
                            <UserAvatar user={user} size="h-5 w-5" />
                            <span className="truncate">{user.displayName}</span>
                        </button>
                    ))}
                </div>
            );
        }
        return null;
    };

    const renderCommentNode = (comment, level = 0) => {
        const isAuthor = currentUser.uid === comment.author?.uid;
        const canDelete = isAuthor || isAdm;
        const commentId = comment.id || comment.createdAt;
        
        // Garante que a comparação seja feita com segurança para comentários legados sem ID
        const isEditing = !!editingComment && (
            (editingComment.id && comment.id) ? editingComment.id === comment.id : editingComment.createdAt === comment.createdAt
        );
        const isReplying = !!replyingToCommentId && replyingToCommentId === commentId;
        
        const indentClass = level === 1 ? 'ml-6 sm:ml-10 border-l border-slate-200 dark:border-slate-800 pl-4 sm:pl-6' : level === 2 ? 'ml-12 sm:ml-20 border-l border-slate-200 dark:border-slate-800 pl-4 sm:pl-6' : '';

        return (
            <div key={commentId} className={`flex flex-col gap-2 ${indentClass} mt-3`}>
                <div className="flex gap-3">
                    <UserAvatar user={usersMap.get(comment.author?.uid)} size="h-7 w-7" />
                    <div className="flex-1 bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/30">
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                                <p className="font-bold text-xs text-slate-800 dark:text-slate-200">{comment.author?.displayName}</p>
                                {level > 0 && (
                                    <span className="text-[9px] bg-slate-200/60 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.2 rounded-full font-semibold">
                                        Resposta
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <p className="text-[10px] text-slate-400 dark:text-slate-500">{new Date(comment.createdAt).toLocaleString('pt-BR')}</p>
                                {!isEditing && (
                                    <div className="flex items-center gap-1">
                                        {level < 2 && (
                                            <button 
                                                type="button" 
                                                onClick={() => { setReplyingToCommentId(commentId); setReplyCommentText(''); }}
                                                className="text-[10px] text-blue-500 hover:text-blue-600 font-bold px-1.5 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/10"
                                            >
                                                Responder
                                            </button>
                                        )}
                                        {isAuthor && (
                                            <button type="button" aria-label="Editar comentário" title="Editar comentário" onClick={() => handleEditComment(comment)} className="text-slate-400 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"><Edit className="h-3 w-3"/></button>
                                        )}
                                        {canDelete && (
                                            <button type="button" aria-label="Excluir comentário" title="Excluir comentário" onClick={() => onDeleteCommentHandler(comment)} className="text-slate-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded p-1"><Trash2 className="h-3 w-3"/></button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {isEditing ? (
                            <div className="relative mt-1">
                                <CommentToolbar 
                                    textareaRef={editCommentTextareaRef} 
                                    textSetter={(text) => setEditingComment(prev => ({ ...prev, text }))}
                                    emojis={emojis}
                                    insertFormatting={insertFormatting}
                                    insertEmoji={insertEmoji}
                                    setActiveAttachmentSetter={setActiveAttachmentSetter}
                                    setActiveAttachmentTextareaRef={setActiveAttachmentTextareaRef}
                                    setShowAttachmentPopover={setShowAttachmentPopover}
                                />
                                <textarea 
                                    ref={editCommentTextareaRef}
                                    value={editingComment.text} 
                                    onChange={(e) => handleTextareaChange(e, (text) => setEditingComment(prev => ({ ...prev, text })), editCommentTextareaRef)}
                                    onKeyDown={(e) => handleTextareaKeyDown(e, filteredUsers, (text) => setEditingComment(prev => ({ ...prev, text })))}
                                    className="input-style px-4 w-full rounded-t-none border-t-0 focus:ring-0 text-xs" 
                                    rows="3"
                                />
                                {renderMentionDropdown(editCommentTextareaRef)}
                                <div className="flex gap-2 mt-2">
                                    <button type="button" onClick={handleSaveEditedComment} className="btn-primary text-[10px] py-1 px-2.5">Salvar</button>
                                    <button type="button" onClick={() => setEditingComment(null)} className="btn-secondary text-[10px] py-1 px-2.5">Cancelar</button>
                                </div>
                            </div>
                        ) : (
                            <div className="prose prose-sm dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 text-xs mt-1">
                                <ReactMarkdown components={markdownComponents}>{preProcessText(comment.text)}</ReactMarkdown>
                            </div>
                        )}
                    </div>
                </div>

                {isReplying && (
                    <div className="ml-8 flex gap-3 mt-1">
                        <UserAvatar user={currentUser} size="h-6 w-6" />
                        <div className="flex-1 relative">
                            <CommentToolbar 
                                textareaRef={replyTextareaRef} 
                                textSetter={setReplyCommentText}
                                emojis={emojis}
                                insertFormatting={insertFormatting}
                                insertEmoji={insertEmoji}
                                setActiveAttachmentSetter={setActiveAttachmentSetter}
                                setActiveAttachmentTextareaRef={setActiveAttachmentTextareaRef}
                                setShowAttachmentPopover={setShowAttachmentPopover}
                            />
                            <textarea
                                ref={replyTextareaRef}
                                value={replyCommentText}
                                onChange={(e) => handleTextareaChange(e, setReplyCommentText, replyTextareaRef)}
                                onKeyDown={(e) => handleTextareaKeyDown(e, filteredUsers, setReplyCommentText)}
                                placeholder={`Responder para ${comment.author?.displayName}...`}
                                rows="2"
                                className="input-style px-4 w-full rounded-t-none border-t-0 focus:ring-0 text-xs"
                                autoFocus
                            />
                            {renderMentionDropdown(replyTextareaRef)}
                            <div className="flex gap-2 mt-2">
                                <button
                                    type="button"
                                    disabled={!replyCommentText.trim() || isProcessing}
                                    onClick={() => handleCommentSubmit(commentId, replyCommentText, setReplyCommentText)}
                                    className="btn-primary text-[10px] py-1 px-2.5 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isProcessing && <Loader className="animate-spin h-3 w-3 mr-1" />}
                                    Responder
                                </button>
                                <button type="button" onClick={() => { setReplyingToCommentId(null); setReplyCommentText(''); }} className="btn-secondary text-[10px] py-1 px-2.5">
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {comment.replies && comment.replies.length > 0 && (
                    <div className="flex flex-col gap-1">
                        {comment.replies.map(reply => renderCommentNode(reply, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    const handleDelete = () => {
        const id = projectIdRef.current;
        if (!isAdm || !id) return;
        onDelete(id);
    };

    const onDeleteCommentHandler = (comment) => {
        const id = projectIdRef.current;
        if (!id) return;
        onDeleteComment(id, comment);
    };

    const onDeleteFileHandler = (file) => {
        const id = projectIdRef.current;
        if (!id) return;
        onDeleteFile(id, file);
    };
    
    const handleDownloadClick = async (filePath, fileName, downloadKey) => {
        if (!filePath || downloading) return;
        setDownloading(downloadKey);
        await onFileDownload(filePath, fileName);
        setDownloading(null);
    };

    const fileCategoryLabels = {
        projeto: "Desenho do Projeto",
        autorizacao: "Autorização de Obra",
        vistoria: "Relatório de Vistoria",
    };
    const mainCategories = ['projeto', 'autorizacao', 'vistoria'];
    const otherFiles = project?.files?.outros || [];

    return (
        <div className="fixed inset-0 bg-black/60 z-[1050] flex justify-center items-center p-4">
            
            {/* Menu de Contexto */}
            {contextMenu.visible && (
                <div
                    className="fixed z-[1060] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 w-56 overflow-hidden"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button onClick={handleOpenGoogleMaps} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <MapPin className="h-4 w-4 text-red-500" />
                        Abrir no Maps
                    </button>
                    <button onClick={handleOpenStreetView} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <ExternalLink className="h-4 w-4 text-yellow-500" />
                        Abrir no Street View
                    </button>
                    <button onClick={handleCopyCoordinates} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <Copy className="h-4 w-4 text-blue-500" />
                        Copiar Coordenadas
                    </button>
                    <button onClick={toggleMapMode} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <Palette className="h-4 w-4 text-green-500" />
                        Alterar modo de cor
                    </button>
                    <button onClick={handleScreenshot} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <Camera className="h-4 w-4 text-purple-500" />
                        Obter screenshot
                    </button>
                </div>
            )}

            {/* Feedback Toast */}
            {feedback.visible && (
                <div
                    className="fixed z-[1070] bg-black/80 text-white px-3 py-1 rounded-md text-sm pointer-events-none transition-opacity duration-300"
                    style={{ top: feedback.y - 40, left: feedback.x }}
                >
                    {feedback.message}
                </div>
            )}

            {/* Overlay transparente para fechar o menu ao clicar fora */}
            {contextMenu.visible && (
                <div className="fixed inset-0 z-[1059]" onClick={closeContextMenu} />
            )}

            <ConfirmDialog
                isOpen={showReplaceConfirm}
                onClose={() => setShowReplaceConfirm(false)}
                onConfirm={handleConfirmReplacePoles}
                title="Confirmar Substituição"
                message={`Você tem certeza? Todos os ${project?.totalPostes || 0} postes atuais serão removidos e substituídos pelos do novo arquivo.`}
                isProcessing={isProcessing}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="Confirmar Exclusão"
                message="Você tem certeza que deseja excluir este projeto? Esta ação não pode ser desfeita."
                isProcessing={isProcessing}
            />

            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                 <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold">{isViewMode ? `Detalhes do Projeto: ${project.protocolo}` : 'Cadastrar Novo Projeto'}</h2>
                    <button onClick={onClose} aria-label="Fechar" title="Fechar" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900"><IconX /></button>
                </header>

                <form id="project-form" onSubmit={handleSubmit} className="flex-grow overflow-y-auto">
                    <div className="border-b border-slate-200 dark:border-slate-700 flex" role="tablist" aria-label="Abas do projeto">
                        <button role="tab" aria-selected={activeModalTab === 'info'} aria-controls="tabpanel-info" type="button" onClick={() => setActiveModalTab('info')} className={`flex-1 py-3 font-semibold flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800 ${activeModalTab === 'info' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-slate-500'}`}>
                            <FileText className="h-5 w-5 sm:mr-2" />
                            <span className="hidden sm:inline">Informações</span>
                        </button>
                        {isViewMode && (
                            <button role="tab" aria-selected={activeModalTab === 'mapa'} aria-controls="tabpanel-mapa" type="button" onClick={() => setActiveModalTab('mapa')} className={`flex-1 py-3 font-semibold flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800 ${activeModalTab === 'mapa' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-slate-500'}`}>
                                <IconMap className="h-5 w-5 sm:mr-2" />
                                <span className="hidden sm:inline">Mapa de Postes</span>
                            </button>
                        )}
                        <button role="tab" aria-selected={activeModalTab === 'arquivos'} aria-controls="tabpanel-arquivos" type="button" onClick={() => setActiveModalTab('arquivos')} className={`flex-1 py-3 font-semibold flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800 ${activeModalTab === 'arquivos' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-slate-500'}`}>
                            <Paperclip className="h-5 w-5 sm:mr-2" />
                            <span className="hidden sm:inline">Arquivos</span>
                        </button>

                        {isViewMode && (
                            <button role="tab" aria-selected={activeModalTab === 'historico'} aria-controls="tabpanel-historico" type="button" onClick={() => setActiveModalTab('historico')} className={`flex-1 py-3 font-semibold flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800 ${activeModalTab === 'historico' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-slate-500'}`}>
                                <History className="h-5 w-5 sm:mr-2" />
                                <span className="hidden sm:inline">Histórico</span>
                            </button>
                        )}
</div>

                    <div className="p-6">
                        {activeModalTab === 'info' && (
                            <fieldset disabled={!isAdm && isViewMode}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Número da Atividade (Protocolo)</label>
                                        <input type="text" name="protocolo" value={formData.protocolo} onChange={handleInputChange} disabled={isViewMode} className="input-style px-4 w-full" required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Status</label>
                                        <select name="status" value={formData.status} onChange={handleInputChange} className="input-style px-4 w-full" required>
                                            {PROJECT_STATUSES.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Cidade</label>
                                        <select name="cidade" value={formData.cidade} onChange={handleInputChange} className="input-style px-4 w-full" required>
                                            <option value="" disabled>Selecione a cidade</option>
                                            {cities.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Local (Bairro/Rua)</label>
                                        <input type="text" name="local" value={formData.local} onChange={handleInputChange} className="input-style px-4 w-full" maxLength="50" placeholder="Ex: Vila Aparecida"/>
                                    </div>
                                    
                                    {isViewMode && (
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Quantidade de Postes</label>
                                            <input type="number" value={formData.totalPostes} disabled className="input-style px-4 w-full bg-slate-100 dark:bg-slate-800" />
                                        </div>
                                    )}

                                    <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Data do Status</label>
                                            <input type="date" name="statusDate" value={formData.statusDate} onChange={handleInputChange} className="input-style px-4 w-full"/>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Data de Vencimento</label>
                                            <input type="date" name="dueDate" value={formData.dueDate} onChange={handleInputChange} className="input-style px-4 w-full"/>
                                        </div>
                                    </div>
                                    {!isViewMode && (
                                        <div className="md:col-span-2">
                                            <StyledFileInput label="Arquivo de Postes (.csv)" onChange={(e) => handleFileChange(e, 'csvFile')} accept=".csv" files={files.csvFile ? [files.csvFile] : []} disabled={!formData.protocolo?.trim()} />
                                            {!formData.protocolo?.trim() && <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">Primeiro, preencha o número do protocolo para habilitar o upload do CSV.</p>}
                                        </div>
                                    )}
                                </div>
                                {isViewMode && (
                                    <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-slate-100 flex items-center">
                                            <MessageSquare className="mr-2"/>
                                            Comentários
                                        </h3>
                                        
                                        <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto pr-2">
                                            {organizedComments.length > 0 ? (
                                                organizedComments.map(comment => renderCommentNode(comment, 0))
                                            ) : (
                                                <p className="text-xs text-slate-400 text-center py-6">Nenhum comentário enviado ainda.</p>
                                            )}
                                        </div>

                                        <div className="flex gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                                            <UserAvatar user={currentUser} size="h-8 w-8" />
                                            <div className="flex-1 relative">
                                                <CommentToolbar 
                                                    textareaRef={newCommentTextareaRef} 
                                                    textSetter={setNewComment}
                                                    emojis={emojis}
                                                    insertFormatting={insertFormatting}
                                                    insertEmoji={insertEmoji}
                                                    setActiveAttachmentSetter={setActiveAttachmentSetter}
                                                    setActiveAttachmentTextareaRef={setActiveAttachmentTextareaRef}
                                                    setShowAttachmentPopover={setShowAttachmentPopover}
                                                />
                                                <textarea 
                                                    ref={newCommentTextareaRef}
                                                    value={newComment}
                                                    onChange={(e) => handleTextareaChange(e, setNewComment, newCommentTextareaRef)}
                                                    onKeyDown={(e) => handleTextareaKeyDown(e, filteredUsers, setNewComment)}
                                                    placeholder="Adicionar um comentário... (suporta Markdown e @menções)"
                                                    rows="3"
                                                    className="input-style px-4 w-full rounded-t-none border-t-0 focus:ring-0 text-xs"
                                                />
                                                {renderMentionDropdown(newCommentTextareaRef)}
                                                <button 
                                                    type="button" 
                                                    disabled={!newComment.trim() || isProcessing} 
                                                    onClick={() => handleCommentSubmit(null, newComment, setNewComment)} 
                                                    className="btn-primary mt-2 flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-xs py-1.5 px-3 transition-colors"
                                                >
                                                    {isProcessing ? <Loader className="animate-spin h-3.5 w-3.5 mr-2" /> : <Send className="h-3.5 w-3.5 mr-2"/>}
                                                    {isProcessing ? 'Enviando...' : 'Comentar'}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Popover de Anexos do Sistema */}
                                        {showAttachmentPopover && (
                                            <div className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4" onClick={() => setShowAttachmentPopover(false)}>
                                                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-md p-4" onClick={e => e.stopPropagation()}>
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                                            <Paperclip className="w-4 h-4 text-blue-500" />
                                                            Anexar Item do Sistema
                                                        </h3>
                                                        <button type="button" onClick={() => setShowAttachmentPopover(false)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"><IconX className="w-4 h-4" /></button>
                                                    </div>
                                                    
                                                    <div className="flex border-b border-slate-200 dark:border-slate-700 mb-4">
                                                        <button
                                                            type="button"
                                                            onClick={() => { setAttachmentType('project'); setAttachmentSearch(''); }}
                                                            className={`flex-1 py-1.5 text-xs font-semibold text-center ${attachmentType === 'project' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-slate-500'}`}
                                                        >
                                                            Projetos
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setAttachmentType('pendency'); setAttachmentSearch(''); }}
                                                            className={`flex-1 py-1.5 text-xs font-semibold text-center ${attachmentType === 'pendency' ? 'border-b-2 border-purple-500 text-purple-500' : 'text-slate-500'}`}
                                                        >
                                                            Pendências
                                                        </button>
                                                    </div>
                                                    
                                                    <input
                                                        type="text"
                                                        value={attachmentSearch}
                                                        onChange={(e) => setAttachmentSearch(e.target.value)}
                                                        placeholder={attachmentType === 'project' ? 'Buscar projeto por protocolo, cidade...' : 'Buscar pendência por descrição, cidade...'}
                                                        className="input-style px-4 w-full mb-3 text-xs"
                                                        autoFocus
                                                    />
                                                    
                                                    <div className="max-h-60 overflow-y-auto border border-slate-100 dark:border-slate-800 rounded-lg divide-y divide-slate-100 dark:divide-slate-800">
                                                        {filteredAttachments.length > 0 ? (
                                                            filteredAttachments.map(item => (
                                                                <button
                                                                    key={item.id}
                                                                    type="button"
                                                                    onClick={() => handleInsertAttachment(item)}
                                                                    className="w-full text-left p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 flex flex-col transition-colors"
                                                                >
                                                                    {attachmentType === 'project' ? (
                                                                        <>
                                                                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{item.protocolo}</span>
                                                                            <span className="text-[10px] text-slate-500">{item.cidade} - {item.local || 'Sem local'}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200">#{item.id.substring(0, 5).toUpperCase()} - {item.cidade}</span>
                                                                            <span className="text-[10px] text-slate-500 line-clamp-1">{item.descricao}</span>
                                                                        </>
                                                                    )}
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <p className="text-[10px] text-slate-400 text-center py-6">Nenhum item encontrado.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </fieldset>
                        )}
                        {activeModalTab === 'mapa' && isViewMode && (
                            <div ref={mapContainerRef}>
                                {project.postes && project.postes.length > 0 ? (
                                    <MapContainer
                                        center={[project.postes[0].latitude, project.postes[0].longitude]}
                                        zoom={13}
                                        zoomSnap={0.1}
                                        zoomDelta={0.1}
                                        style={{ height: '500px', width: '100%' }}
                                        className={`rounded-lg ${isMapDark ? 'map-dark' : ''}`}
                                    >
                                        <MapEvents onContextMenu={handleContextMenu} />
                                        <TileLayer url={tileUrl} attribution={tileAttribution} />
                                        {project.postes.map((poste, idx) => (
                                            <Marker key={idx} position={[poste.latitude, poste.longitude]} icon={createStatusIcon(project.status)}>
                                                <Popup>
                                                    <b>Endereço:</b> {poste.endereco || 'N/A'}<br />
                                                    <b>Protocolo Lic.:</b> {poste.protocoloLicenciamento || 'N/A'}
                                                </Popup>
                                            </Marker>
                                        ))}
                                    </MapContainer>
                                ) : <p>Nenhum poste com coordenadas encontrado para este projeto.</p>}
                            </div>
                        )}
                        {activeModalTab === 'arquivos' && (
                            <div className="space-y-6">
                                {isViewMode ? (
                                    <React.Fragment>
                                        <div>
                                            <h4 className="text-lg font-semibold mb-4">Arquivos do Projeto</h4>
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    {mainCategories.map(cat => {
                                                        const file = project.files?.[cat];
                                                        const newFile = newCategorizedFiles[cat];
                                                        const downloadKey = `main-${cat}`;
                                                        return (
                                                            <div key={cat} className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex flex-col justify-between">
                                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">{fileCategoryLabels[cat]}</p>
                                                                <div className="mt-auto">
                                                                    {file ? (
                                                                        <div className="flex items-center justify-between gap-2">
                                                                            <button type="button" onClick={() => handleDownloadClick(file.path, file.name, downloadKey)} disabled={downloading === downloadKey} className="btn-secondary text-sm flex-grow justify-center flex items-center" aria-label={`Baixar arquivo: ${file.name}`}>
                                                                                {downloading === downloadKey ? <Loader className="animate-spin h-4 w-4 mr-2"/> : <Download className="h-4 w-4 mr-2" />}
                                                                                Baixar
                                                                            </button>
                                                                            {isAdm && (
                                                                                <button type="button" onClick={() => onDeleteFileHandler(file)} className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-slate-800" title="Excluir arquivo" aria-label="Excluir arquivo">
                                                                                    <Trash2 className="h-4 w-4" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    ) : newFile ? (
                                                                        <div className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-medium p-2 rounded-md truncate" title={newFile.name}>
                                                                            {newFile.name}
                                                                        </div>
                                                                    ) : (
                                                                        <label
                                                                            className="btn-secondary text-sm flex items-center justify-center cursor-pointer w-full focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 dark:focus-within:ring-offset-slate-900"
                                                                            tabIndex="0"
                                                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.target.querySelector('input').click(); }}
                                                                        >
                                                                            <UploadCloud className="h-4 w-4 mr-2" />
                                                                            Anexar
                                                                            <input type="file" className="sr-only" tabIndex="-1" onChange={(e) => handleCategorizedFileChange(e, cat)} accept={cat === 'projeto' ? '.dwg,.pdf' : '.pdf'} />
                                                                        </label>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                
                                                {otherFiles.length > 0 && (
                                                    <div>
                                                        <h5 className="text-md font-semibold mb-2 mt-4 border-t border-slate-200 dark:border-slate-700 pt-4">Outros Arquivos</h5>
                                                        <ul className="list-disc list-inside space-y-2">
                                                            {otherFiles.map((file, idx) => {
                                                                const downloadKey = `other-${idx}`;
                                                                return(
                                                                    <li key={idx} className="flex items-center justify-between p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                                                                        <button type="button" onClick={() => handleDownloadClick(file.path, file.name, downloadKey)} disabled={!!downloading} className="text-blue-500 hover:underline disabled:text-slate-400 flex-grow text-left flex items-center gap-2">
                                                                            {downloading === downloadKey ? <Loader className="animate-spin h-4 w-4"/> : <Paperclip className="h-4 w-4"/>}
                                                                            {file.name}
                                                                        </button>
                                                                        {isAdm && (
                                                                            <button type="button" onClick={() => onDeleteFileHandler(file)} className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors ml-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-slate-800" title="Excluir arquivo" aria-label="Excluir arquivo">
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </button>
                                                                        )}
                                                                    </li>
                                                                )
                                                            })}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold mb-2 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">Anexar Novos Arquivos (Outros)</h4>
                                            <StyledFileInput label="Anexar novos arquivos (serão adicionados em 'Outros')" onChange={handleNewFilesChange} isMultiple={true} files={newFiles} disabled={!isAdm} />
                                        </div>
                                        
                                        {isAdm && (
                                            <div className="mt-6 pt-6 border-t border-red-500/30 dark:border-red-500/20">
                                                <h4 className="text-lg font-semibold mb-2 text-red-600 dark:text-red-400 flex items-center">
                                                    <AlertTriangle className="mr-2" />
                                                    Substituir Planilha de Postes
                                                </h4>
                                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                                    Atenção: Esta ação irá <strong>excluir todos os postes atuais</strong> deste projeto e substituí-los pelos postes do novo arquivo CSV. Esta operação não pode ser desfeita.
                                                </p>
                                                <StyledFileInput
                                                    label="Novo Arquivo de Postes (.csv)"
                                                    onChange={(e) => setNewCsvFile(e.target.files[0])}
                                                    accept=".csv"
                                                    files={newCsvFile ? [newCsvFile] : []}
                                                />
                                                {newCsvFile && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowReplaceConfirm(true)}
                                                        className="btn-primary bg-amber-600 hover:bg-amber-700 mt-4"
                                                        disabled={isProcessing}
                                                    >
                                                        {isProcessing ? 'Processando...' : 'Substituir Postes'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <StyledFileInput label="Projeto (.dwg, .pdf)" onChange={(e) => handleFileChange(e, 'projeto')} accept=".dwg,.pdf" files={files.projeto ? [files.projeto] : []} />
                                        <StyledFileInput label="Autorização de Obra (.pdf)" onChange={(e) => handleFileChange(e, 'autorizacao')} accept=".pdf" files={files.autorizacao ? [files.autorizacao] : []} />
                                        <StyledFileInput label="Vistoria (.pdf)" onChange={(e) => handleFileChange(e, 'vistoria')} accept=".pdf" files={files.vistoria ? [files.vistoria] : []} />
                                        <StyledFileInput label="Outros (múltiplos)" onChange={(e) => handleFileChange(e, 'outros')} isMultiple={true} files={files.outros} />
                                    </div>
                                )}
                            </div>
                        )}

                        {activeModalTab === 'historico' && isViewMode && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                                    Histórico de Alterações de Status
                                </h3>
                                {isHistoryLoading ? (
                                    <div className="flex justify-center items-center h-48">
                                        <Loader className="animate-spin h-8 w-8 text-slate-500" />
                                    </div>
                                ) : (
                                    <ProjectStatusTimeline statusHistory={statusHistory} usersMap={usersMap} currentStatus={project.status} />
                                )}
                            </div>
                        )}
                    </div>
                </form>
                <footer className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center gap-4">
                    <div>
                        {isViewMode && isAdm && (
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={isProcessing}
                                className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                                title="Excluir Projeto"
                                aria-label="Excluir Projeto"
                            >
                                <Trash2 className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-x-2 sm:gap-x-4">
                        <button type="button" onClick={onClose} className="btn-secondary" title="Cancelar" aria-label="Cancelar">
                            <span className="hidden sm:inline">Cancelar</span>
                            <IconX className="sm:hidden h-5 w-5" />
                        </button>
                        {isAdm && (
                            <button type="submit" form="project-form" disabled={isProcessing} className="btn-primary flex items-center">
                                <Save className="h-5 w-5 sm:mr-2" />
                                <span className="hidden sm:inline">
                                    {isViewMode ? (isProcessing ? 'Salvando...' : 'Salvar Alterações') : (isProcessing ? 'Salvando...' : 'Salvar Projeto')}
                                </span>
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default React.memo(ProjectModal);
